import React from 'react';
import { useUIDSeed } from 'react-uid';

import subYears from 'date-fns/sub_years';

import { useFormik } from 'formik';
import Postcode from 'postcode';
import * as Yup from 'yup';

import { getCheckoutUrlWithProductParam } from '@zego/flows/onlineCheckout/redirects';
import { FormContext } from '@zego/forms/Form';
import FormActions from '@zego/forms/FormActions';
import FormField from '@zego/forms/FormField';
import FormFieldGroup from '@zego/forms/FormFieldGroup';
import Input from '@zego/forms/Input';
import SegmentedChoiceInput from '@zego/forms/SegmentedChoiceInput';
import SplitDateInput from '@zego/forms/SplitDateInput';
import getStorage from '@zego/getStorage';
import { BREAKPOINTS, primaryColor, unit } from '@zego/theme';
import { trackEvent } from '@zego/tracking';

import RegularText from '@zego/components/atoms/RegularText';
import View from '@zego/components/atoms/View';
import Button from '@zego/components/button';
import Link from '@zego/components/Link';
import ScopedNotification, {
  UiStatus,
} from '@zego/components/ScopedNotification';

const INITIAL_VALUES_KEY = 'taxiQuickEstimatesInitialValues';

function Form(props) {
  const seed = useUIDSeed();

  return (
    <FormContext.Provider value={seed}>
      <form {...props} />
    </FormContext.Provider>
  );
}

function getDefaultInitialValues() {
  const storage = getStorage('local');
  const defaultInitialValues = storage && storage.getItem(INITIAL_VALUES_KEY);

  if (defaultInitialValues) {
    return defaultInitialValues;
  }

  return {
    noClaimsDuration: '',
    bday: '',
    postcode: '',
  };
}

const validationSchema = Yup.object().shape({
  noClaimsDuration: Yup.string().required('Required'),
  bday: Yup.date()
    .required('Required')
    .min(subYears(new Date(), 90), 'Too long ago')
    .max(new Date(), 'Cannot be in the future'),
  postcode: Yup.string()
    .required('Required')
    .test('is-valid-postcode', 'Must be a valid UK postcode', value => {
      if (!value || typeof value !== 'string') {
        return false;
      }
      return Postcode.isValid(value.trim());
    }),
});

export default function PrivateHireQuickEstimatesFormCore({
  submitting,
  onSubmit,
  className,
  initialValues = getDefaultInitialValues(),
  error,
  id,
  buttonLabel = 'Get your estimate',
  source,
}) {
  const handleSubmit = (values, actions) => {
    const storage = getStorage('local');
    storage && storage.setItem(INITIAL_VALUES_KEY, values);
    onSubmit(values, () => actions.setSubmitting(false));
  };

  const showRetrieveQuote = source !== 'OLA_UK';

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <Form id={id} onSubmit={formik.handleSubmit} className={className}>
      {error && (
        <ScopedNotification
          status={UiStatus.Error}
          message="Something went wrong."
        />
      )}
      <FormFieldGroup spacing={2}>
        <View
          css={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            [BREAKPOINTS.large]: { flexDirection: 'row' },
          }}
        >
          <FormField
            name="bday"
            label="Date of birth"
            value={formik.values.bday}
            error={formik.errors.bday}
            widget={SplitDateInput}
            widgetProps={{
              showDay: true,
            }}
            onChange={value => formik.setFieldValue('bday', value, false)}
            css={{
              [BREAKPOINTS.large]: {
                width: 'calc(60% - 20px)',
                marginRight: 20,
              },
            }}
          />

          <FormField
            name="postcode"
            placeholder="WR2 D2E"
            label="Postcode"
            textTransform="uppercase"
            value={formik.values.postcode}
            error={formik.errors.postcode}
            widget={Input}
            onChange={event =>
              formik.setFieldValue('postcode', event.target.value, false)
            }
            css={{
              marginTop: unit(3),
              [BREAKPOINTS.large]: { marginTop: 0, width: '40%' },
            }}
          />
        </View>
        <FormField
          name="noClaimsDuration"
          label="Select your Private Hire No Claims Discount years"
          widget={SegmentedChoiceInput}
          widgetProps={{
            choices: [
              { label: '0', value: '0' },
              { label: '1', value: '1' },
              { label: '2', value: '2' },
              { label: '3', value: '3' },
              { label: '4', value: '4' },
              { label: '5+', value: '5' },
            ],
          }}
          value={formik.values.noClaimsDuration}
          error={formik.errors.noClaimsDuration}
          onChange={event =>
            formik.setFieldValue('noClaimsDuration', event.target.value, false)
          }
        />
      </FormFieldGroup>

      <RegularText
        css={{
          marginTop: unit(3),
          marginBottom: 0,
          fontSize: 12,
          fontWeight: 'normal',
          textAlign: 'center',
        }}
      >
        By continuing you are agreeing to our{' '}
        <Link href="/privacy">Privacy Policy.</Link>
      </RegularText>
      <FormActions>
        <Button type="submit" stretch={true} submitting={submitting}>
          {error ? 'Try again' : buttonLabel}
        </Button>
      </FormActions>
      {showRetrieveQuote && (
        <Link
          href={getCheckoutUrlWithProductParam('GB.PRIVATE_HIRE')}
          onClick={() => {
            trackEvent('Private Hire - Retrieve your Quote Clicked');
          }}
          css={{
            display: 'block',
            color: primaryColor(11),
            cursor: 'pointer',
            padding: unit(1),
            textAlign: 'center',
          }}
        >
          Retrieve your quote
        </Link>
      )}
    </Form>
  );
}
